<script setup lang="ts">
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Quote, CheckCircle } from 'lucide-vue-next';
import { ref, computed, onMounted, onUnmounted } from 'vue';

interface Review {
    id: number;
    author_name: string;
    rating: number;
    comment: string;
    service?: string;
    source: 'bark' | 'google' | 'facebook' | 'manual';
    review_date: string;
    is_verified: boolean;
}

interface Props {
    reviews: Review[];
    autoplay?: boolean;
    interval?: number;
}

const props = withDefaults(defineProps<Props>(), {
    autoplay: true,
    interval: 5000,
});

const currentIndex = ref(0);
let autoplayTimer: ReturnType<typeof setInterval> | null = null;

const visibleReviews = computed(() => {
    if (props.reviews.length <= 3) {
        return props.reviews;
    }
    
    const reviews = [];
    for (let i = 0; i < 3; i++) {
        const index = (currentIndex.value + i) % props.reviews.length;
        reviews.push(props.reviews[index]);
    }
    return reviews;
});

const next = () => {
    currentIndex.value = (currentIndex.value + 1) % props.reviews.length;
};

const prev = () => {
    currentIndex.value = (currentIndex.value - 1 + props.reviews.length) % props.reviews.length;
};

const goTo = (index: number) => {
    currentIndex.value = index;
};

const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
        month: 'long',
        year: 'numeric',
    });
};

const getSourceBadge = (source: string) => {
    const badges = {
        bark: { label: 'Bark', class: 'bg-purple-100 text-purple-700' },
        google: { label: 'Google', class: 'bg-blue-100 text-blue-700' },
        facebook: { label: 'Facebook', class: 'bg-blue-100 text-blue-700' },
        manual: { label: 'Client', class: 'bg-gray-100 text-gray-700' },
    };
    return badges[source as keyof typeof badges] || badges.manual;
};

onMounted(() => {
    if (props.autoplay && props.reviews.length > 3) {
        autoplayTimer = setInterval(next, props.interval);
    }
});

onUnmounted(() => {
    if (autoplayTimer) {
        clearInterval(autoplayTimer);
    }
});
</script>

<template>
    <div class="relative">
        <!-- Reviews Grid -->
        <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card
                v-for="review in visibleReviews"
                :key="review.id"
                class="relative overflow-hidden transition-all hover:shadow-lg"
            >
                <CardContent class="p-6 space-y-4">
                    <!-- Quote Icon -->
                    <div class="absolute top-4 right-4 opacity-10">
                        <Quote class="h-16 w-16 text-accent" />
                    </div>

                    <!-- Header -->
                    <div class="flex items-start justify-between relative z-10">
                        <div>
                            <div class="flex items-center gap-2">
                                <h4 class="font-semibold">{{ review.author_name }}</h4>
                                <CheckCircle v-if="review.is_verified" class="h-4 w-4 text-accent" />
                            </div>
                            <p class="text-sm text-muted-foreground">{{ formatDate(review.review_date) }}</p>
                        </div>
                        <Badge :class="getSourceBadge(review.source).class" variant="secondary" class="text-xs">
                            {{ getSourceBadge(review.source).label }}
                        </Badge>
                    </div>

                    <!-- Rating -->
                    <div class="flex gap-1">
                        <Star
                            v-for="i in 5"
                            :key="i"
                            :class="i <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'"
                            class="h-5 w-5"
                        />
                    </div>

                    <!-- Comment -->
                    <p class="text-sm leading-relaxed text-foreground/80">
                        "{{ review.comment }}"
                    </p>

                    <!-- Service -->
                    <div v-if="review.service" class="pt-2 border-t">
                        <p class="text-xs text-muted-foreground">
                            Service : <span class="font-medium">{{ review.service }}</span>
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>

        <!-- Navigation (si plus de 3 avis) -->
        <div v-if="reviews.length > 3" class="flex items-center justify-center gap-4 mt-8">
            <button
                @click="prev"
                class="p-2 rounded-full bg-accent/10 hover:bg-accent/20 transition-colors"
                aria-label="Avis précédent"
            >
                <svg class="h-5 w-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            <!-- Dots -->
            <div class="flex gap-2">
                <button
                    v-for="(_, index) in reviews"
                    :key="index"
                    @click="goTo(index)"
                    :class="index === currentIndex ? 'bg-accent w-8' : 'bg-accent/30 w-2'"
                    class="h-2 rounded-full transition-all"
                    :aria-label="`Aller à l'avis ${index + 1}`"
                />
            </div>

            <button
                @click="next"
                class="p-2 rounded-full bg-accent/10 hover:bg-accent/20 transition-colors"
                aria-label="Avis suivant"
            >
                <svg class="h-5 w-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
            </button>
        </div>

        <!-- Bark Badge -->
        <div class="mt-8 text-center">
            <a
                href="https://www.bark.com/fr/fr/b/lyra-events/9XRLLO/"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors"
            >
                <span>Voir tous nos avis sur</span>
                <Badge variant="outline" class="gap-1">
                    <Star class="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    <span class="font-semibold">Bark</span>
                    <span class="text-xs">5/5 (10 avis)</span>
                </Badge>
            </a>
        </div>
    </div>
</template>
